import { useContext, useMemo, useCallback } from 'react';
import { AppContext } from 'App';
import { ActionPermission, can as hasAccessTo, ICurrentUser, UserType } from 'lib/specialPermissions';

interface UseCurrentUserHookInterface {
  isAdmin: boolean;
  isDirector: boolean;
  isOwner: boolean;
  isTeacher: boolean;
  isOwnerOrAdmin: boolean;
  isOwnerOrAdminOrDirector: boolean;
  isStaffUser: boolean;
  isFreemiumUser: boolean;
  isDemoUser: boolean;
  user: ICurrentUser,
  can: (action: ActionPermission) => boolean;
}

/**
 * Hook to get the current user information and some helper flags
 * to know if this is a director, teacher, admin, owner, etc.
 *
 * @return UseCurrentHookInterface
 */
const useCurrentUser = (): UseCurrentUserHookInterface => {
  const { user, isFreemiumUser, isDemoUser } = useContext<any>(AppContext);

  const can = useCallback((action: ActionPermission) => user
    ? hasAccessTo(user, action) : false, [user]);

  const isOwnerOrAdmin = useMemo(() => [
    UserType.OWNER,
    UserType.ADMIN,
  ].includes(user?.type), [user]);

  const isOwnerOrAdminOrDirector = useMemo(() => [
    UserType.OWNER,
    UserType.ADMIN,
    UserType.DIRECTOR,
  ].includes(user?.type), [user]);


  const isAdmin = useMemo(() => user?.type === UserType.ADMIN, [user]);

  const isDirector = useMemo(() => user?.type === UserType.DIRECTOR, [user]);

  const isTeacher = useMemo(() => user?.type === UserType.TEACHER, [user]);

  const isOwner = useMemo(() => user?.type === UserType.OWNER, [user]);

  const isStaffUser = useMemo(() => isOwnerOrAdminOrDirector || isTeacher,
    [isTeacher, isOwnerOrAdminOrDirector]);

  return {
    can,
    user,
    isAdmin,
    isOwner,
    isTeacher,
    isDirector,
    isDemoUser,
    isFreemiumUser,
    isOwnerOrAdmin,
    isOwnerOrAdminOrDirector,
    isStaffUser,
  };
};

export default useCurrentUser;
